import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { CalendarToday, AccessTime } from '@mui/icons-material';
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import { PER_PAGE_OPTIONS } from "../../../constants/pagination";

const UserCancellationTable = ({
  data = [],
  searchText = "",
  pagination = { total: 0, page: 0, page_size: 10, total_pages: 0 },
  onPageChange,
  onRowsPerPageChange
}) => {
  const loading = useSelector((state) => state.user_cancellations.isLoading);

  const filteredReports = data.filter(report =>
    report.reason?.toLowerCase().includes(searchText.toLowerCase()) ||
    report.user_email?.toLowerCase().includes(searchText.toLowerCase()) ||
    report.user_full_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return <LoadingBackdrop />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width="120px"><strong>Ref. #</strong></TableCell>
              <TableCell><strong>Reason</strong></TableCell>
              <TableCell width="280px" sx={{ whiteSpace: 'nowrap' }}><strong>User</strong></TableCell>
              <TableCell width="140px"><strong>Date & Time</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredReports || filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {searchText ? "No cancellation reports found matching your search" : "No cancellation reports available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredReports.map((report) => {
                const dt = new Date(report.created_at);

                return (
                  <TableRow
                    key={report.user_cancellation_id}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell width="120px">{report.user_cancellation_id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {report.reason}
                      </Typography>
                    </TableCell>
                    <TableCell width="280px" sx={{ whiteSpace: 'nowrap' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.user_full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.user_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell width="140px">
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {dt.toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTime sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {dt.toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination?.total || 0}
          page={(pagination?.page || 1) - 1}
          onPageChange={onPageChange}
          rowsPerPage={pagination?.page_size || 10}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          labelRowsPerPage="Page Size:"
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  );
};

export default UserCancellationTable; 